import {
  collection,
  getDocs,
  limit,
  orderBy,
  Query,
  query,
  QueryDocumentSnapshot,
  QuerySnapshot,
  startAfter as fsStartAfter,
  where,
} from 'firebase/firestore';
import sortBy from 'lodash/sortBy';
import { collection as rxCollection } from 'rxfire/firestore';
import { from, Observable } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { EntityType, IEntitySessionMetadata, IUser } from '../types';
import { authenticatedUserProvider } from './userProvider';

export const sessionsProvider = (
  entityId: string,
  entityType: EntityType
): Observable<IEntitySessionMetadata[]> => {
  return authenticatedUserProvider().pipe(
    switchMap((user: IUser): Observable<QueryDocumentSnapshot[]> => {
      return rxCollection(
        query(
          collection(
            user.services.firestore,
            `orgs/${user.orgId}/${entityType}/${entityId}/sessions`
          ),
          where('count', '>', 0)
        )
      );
    }),
    map((docs: QueryDocumentSnapshot[]): IEntitySessionMetadata[] => {
      return sortBy(docs, (d: QueryDocumentSnapshot): number => -+d.id).map(
        (d: QueryDocumentSnapshot): IEntitySessionMetadata => {
          return {
            count: d.data().count,
            id: d.id.toString(),
          };
        }
      );
    })
  );
};

/**
 * @description infinite fetch of sessions for the supplied entity
 * @param entityId
 * @param entityType
 * @param startAfter
 * @returns
 */
export const infiniteSessions = async (
  entityId: string,
  entityType: EntityType,
  startAfter?: number
): Promise<IEntitySessionMetadata[]> => {
  return authenticatedUserProvider()
    .pipe(
      switchMap((user: IUser): Observable<IEntitySessionMetadata[]> => {
        let mQuery: Query;
        if (!startAfter) {
          mQuery = query(
            collection(
              user.services.firestore,
              `orgs/${user.orgId}/${entityType}/${entityId}/sessions`
            ),

            orderBy('id', 'desc'),
            limit(100)
          );
        } else {
          mQuery = query(
            collection(
              user.services.firestore,
              `orgs/${user.orgId}/${entityType}/${entityId}/sessions`
            ),

            orderBy('id', 'desc'),
            fsStartAfter(startAfter),
            limit(100)
          );
        }
        return from(
          getDocs(mQuery).then(
            (snap: QuerySnapshot): QueryDocumentSnapshot[] => snap.docs
          )
        ).pipe(
          map((docs: QueryDocumentSnapshot[]): IEntitySessionMetadata[] => {
            return docs
              .map((d: QueryDocumentSnapshot): IEntitySessionMetadata => {
                return {
                  count: d.data().count,
                  id: d.data().id.toString(),
                };
              })
              .filter(
                (session: IEntitySessionMetadata): boolean => session.count > 0
              );
          })
        );
      }),
      take(1)
    )
    .toPromise();
};
